import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Snackbar, Alert, Chip, CircularProgress, Grid, Card, CardContent, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Refresh, Speed, Error, CheckCircle, Timeline } from '@mui/icons-material';
import { getAPIStatistics } from '../../services/apiManagement';

const APIStatistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAPIStatistics({ days });
      setStats(response.data || {});
    } catch (error) {
      console.error('Error fetching API statistics:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch API statistics',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleRefresh = () => {
    fetchStats();
  };

  const handleDaysChange = (event) => {
    setDays(event.target.value);
  };

  const formatResponseTime = (time) => {
    if (!time) return '0ms';
    return `${(time * 1000).toFixed(2)}ms`;
  };

  const googleStats = stats?.google_translation || {};
  const translationStats = stats?.translation || {};
  const translationProviderRows = translationStats.by_provider || [];
  const formatCount = (value, digits = 0) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });

  const getSuccessRate = () => {
    if (!stats || stats.total_requests === 0) return 0;
    return ((stats.successful_requests / stats.total_requests) * 100).toFixed(1);
  };

  const getErrorRate = () => {
    if (!stats || stats.total_requests === 0) return 0;
    return (((stats.logged_failures ?? stats.failed_requests ?? 0) / stats.total_requests) * 100).toFixed(1);
  };

  const formatDateRange = () => {
    if (!stats?.date_range) return '';
    const start = new Date(stats.date_range.start).toLocaleDateString();
    const end = new Date(stats.date_range.end).toLocaleDateString();
    return `${start} - ${end}`;
  };

  if (loading && !stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            API Statistics
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Comprehensive analytics and performance metrics for API usage
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={days}
              label="Time Range"
              onChange={handleDaysChange}
            >
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
              <MenuItem value={365}>Last year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Date Range Info */}
      {stats?.date_range && (
        <Box sx={{ mb: 3 }}>
          <Chip
            label={`Date Range: ${formatDateRange()} (${stats.date_range.days} days)`}
            color="primary"
            variant="outlined"
          />
        </Box>
      )}

      {/* Main Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {stats?.total_requests || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Requests
                  </Typography>
                </Box>
                <Timeline color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats?.successful_requests || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Successful ({getSuccessRate()}%)
                  </Typography>
                </Box>
                <CheckCircle color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {stats?.logged_failures ?? stats?.failed_requests ?? 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Failed ({getErrorRate()}%)
                  </Typography>
                </Box>
                <Error color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {formatResponseTime(stats?.average_response_time)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Avg Response Time
                  </Typography>
                </Box>
                <Speed color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Statistics */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Provider Usage
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {stats?.total_providers_used || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Different providers used
                </Typography>
              </Box>
              {stats?.most_used_provider && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="textSecondary">
                    Most used:
                  </Typography>
                  <Chip
                    label={stats.most_used_provider.service_name || stats.most_used_provider.provider_name || 'Unknown'}
                    color="primary"
                    size="small"
                  />
                  <Typography variant="body2" color="textSecondary">
                    ({stats.most_used_provider.count} requests)
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Endpoint Performance
              </Typography>
              {stats?.most_used_endpoint ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Most called endpoint:
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', mb: 1 }}>
                    {stats.most_used_endpoint.endpoint}
                  </Typography>
                  <Chip
                    label={`${stats.most_used_endpoint.count} calls`}
                    color="secondary"
                    size="small"
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No endpoint data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Insights */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Translation Provider Usage
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="primary">{formatCount(translationStats.total_requests)}</Typography>
                  <Typography variant="body2" color="textSecondary">Provider API Calls</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="success.main">{formatCount(translationStats.cache_hits)}</Typography>
                  <Typography variant="body2" color="textSecondary">Cache Hits</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="info.main">{formatCount(translationStats.total_source_characters)}</Typography>
                  <Typography variant="body2" color="textSecondary">Source Characters</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="warning.main">{formatCount(translationStats.total_billed_characters)}</Typography>
                  <Typography variant="body2" color="textSecondary">Billed Characters</Typography>
                </Grid>
              </Grid>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                {translationProviderRows.map((provider) => (
                  <Box key={provider.translation_provider} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {provider.translation_provider}
                      </Typography>
                      <Chip label={`${formatCount(provider.total_requests)} API calls`} size="small" color="primary" variant="outlined" />
                    </Box>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="textSecondary">Logged</Typography>
                        <Typography variant="body2" fontWeight="bold">{formatCount(provider.logged_requests ?? provider.total_requests)}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="textSecondary">Skipped</Typography>
                        <Typography variant="body2" fontWeight="bold" color="warning.main">{formatCount(provider.skipped_requests)}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="textSecondary">Today</Typography>
                        <Typography variant="body2" fontWeight="bold">{formatCount(provider.characters_today)} chars</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="textSecondary">This Month</Typography>
                        <Typography variant="body2" fontWeight="bold">{formatCount(provider.characters_this_month)} chars</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="textSecondary">Soft Limit</Typography>
                        <Typography variant="body2" fontWeight="bold">{provider.soft_limit ? formatCount(provider.soft_limit) : 'N/A'}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="textSecondary">Remaining</Typography>
                        <Typography variant="body2" fontWeight="bold">{provider.remaining_soft_limit_capacity !== null ? formatCount(provider.remaining_soft_limit_capacity) : 'N/A'}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="textSecondary">Failures</Typography>
                        <Typography variant="body2" fontWeight="bold" color="error.main">{formatCount(provider.failed_requests)}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="textSecondary">Cache Hits</Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">{formatCount(provider.cache_hits)}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                {translationProviderRows.length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    No translation provider usage is available for this time range.
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Performance Insights
              </Typography>
              <Box sx={{
                width: '100%',
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: 8,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: 'rgba(0,0,0,0.5)',
                },
              }}>
                <Grid container spacing={2} sx={{ minWidth: 600 }}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h6" color="success.main" fontWeight="bold">
                        {getSuccessRate()}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Success Rate
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h6" color="error.main" fontWeight="bold">
                        {getErrorRate()}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Error Rate
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h6" color="info.main" fontWeight="bold">
                        {stats?.total_requests > 0 ? Math.round(stats.total_requests / stats.date_range.days) : 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Avg Requests/Day
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h6" color="warning.main" fontWeight="bold">
                        {stats?.total_providers_used || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Active Providers
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Google Translation Health
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="primary">{formatCount(googleStats.total_requests)}</Typography>
                  <Typography variant="body2" color="textSecondary">Real Google Calls</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="success.main">{formatCount(googleStats.successful_requests)}</Typography>
                  <Typography variant="body2" color="textSecondary">Successful</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="error.main">{formatCount(googleStats.failed_requests)}</Typography>
                  <Typography variant="body2" color="textSecondary">Failed</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="info.main">{formatResponseTime(googleStats.average_response_time)}</Typography>
                  <Typography variant="body2" color="textSecondary">Avg Response Time</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="primary">{formatCount(googleStats.total_characters)}</Typography>
                  <Typography variant="body2" color="textSecondary">Total Characters</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="success.main">{formatCount(googleStats.successful_characters)}</Typography>
                  <Typography variant="body2" color="textSecondary">Successful Characters</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="error.main">{formatCount(googleStats.failed_characters)}</Typography>
                  <Typography variant="body2" color="textSecondary">Failed Characters</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="info.main">{formatCount(googleStats.average_characters_per_request, 1)}</Typography>
                  <Typography variant="body2" color="textSecondary">Avg Characters/Attempt</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="warning.main">{formatCount(googleStats.skipped_requests)}</Typography>
                  <Typography variant="body2" color="textSecondary">Skipped Checks</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="h5" fontWeight="bold" color="text.primary">{formatCount(googleStats.logged_requests ?? googleStats.total_requests)}</Typography>
                  <Typography variant="body2" color="textSecondary">Logged Rows</Typography>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                {(googleStats.by_error_category || []).filter(item => item.metadata__error_category).map((item) => (
                  <Chip key={item.metadata__error_category} label={`${item.metadata__error_category}: ${item.count} calls, ${formatCount(item.total_characters)} chars`} size="small" color="warning" variant="outlined" />
                ))}
                {(googleStats.by_origin || []).filter(item => item.metadata__origin).map((item) => (
                  <Chip key={item.metadata__origin} label={`${item.metadata__origin}: ${item.count} calls, ${formatCount(item.total_characters)} chars (${item.failed_requests} failed)`} size="small" variant="outlined" />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* No Data State */}
      {!loading && (!stats || stats.total_requests === 0) && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Timeline sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No API Statistics Available
          </Typography>
          <Typography variant="body2" color="textSecondary">
            API calls will appear here once they are made within the selected time range.
          </Typography>
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default APIStatistics;
